import type { MetricDataType } from '@/entities/optimization-statistic'
import type { MetricsDataUnit } from '../model/types'

import { filter, isNumber, max, values } from 'lodash/fp'
import { ChevronDownIcon, ChevronRightIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import ProgressRow from '@/shared/components/Progress/ProgressRow'
import { Button } from '@/shared/components/ui/button'
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from '@/shared/components/ui/collapsible'
import { DeltaValue } from '@/shared/components/ui/delta-value'

import {
	formatAbsoluteValue,
	formatTotalDiffValue,
	getTranslationKey,
} from '../lib/helpers'
import { useOpenStateContext } from './OpenStateProvider'

export const MetricRow = ({ item }: { item: FormattedMetric }) => {
	const { t } = useTranslation()

	const { isOpen, toggle } = useOpenStateContext()
	const open = isOpen(item.key) || false
	const ArrowIcon = open ? ChevronDownIcon : ChevronRightIcon
	const maxValue = max(filter(isNumber, values(item.values))) || 0
	const translationKey = getTranslationKey(item.key, item.widgetType)
	const trimmedDescription = item.description.substring(0, 30)

	return (
		<Collapsible open={open} onOpenChange={() => toggle(item.key)}>
			<div className='relative grid gap-2 overflow-hidden'>
				<div className='flex items-center gap-1'>
					<CollapsibleTrigger asChild>
						<Button size='icon-xs' variant='ghost'>
							<ArrowIcon className='opacity-50' />
						</Button>
					</CollapsibleTrigger>
					<span className='text-sm font-medium'>
						{t(translationKey, { defaultValue: trimmedDescription })}
					</span>
					<div className='ml-auto'>
						<DeltaValue className='text-sm font-medium' value={item.diff.value}>
							{formatTotalDiffValue(
								item.diff.value,
								item.diff.type,
								t,
								item.currency
							)}
						</DeltaValue>
					</div>
				</div>
				<CollapsibleContent className='relative mb-4 grid gap-2 pl-5'>
					<ProgressRow
						disableTooltip
						barClassName='bg-slate-300'
						label={t('status_panel_current')}
						relativeValue={((item.values['init'] || 0) / maxValue) * 100}
						absoluteValue={formatAbsoluteValue(
							item.values['init'] || 0,
							item.data.type,
							item.currency
						)}
					/>
					<ProgressRow
						disableTooltip
						barClassName='bg-primary-200'
						label={t('status_panel_optimized')}
						relativeValue={((item.values['optimal'] || 0) / maxValue) * 100}
						absoluteValue={formatAbsoluteValue(
							item.values['optimal'] || 0,
							item.data.type,
							item.currency
						)}
					/>
					<ProgressRow
						disableTooltip
						label='Final'
						relativeValue={((item.values['final'] || 0) / maxValue) * 100}
						absoluteValue={formatAbsoluteValue(
							item.values['final'] || 0,
							item.data.type,
							item.currency
						)}
					/>
				</CollapsibleContent>
			</div>
		</Collapsible>
	)
}

export type FormattedMetric = {
	widgetType?: string
	key: string
	data: {
		type: MetricDataType
		values: MetricsDataUnit
	}
	diff: {
		type: MetricDataType
		value: number
	}
	values: MetricsDataUnit
	description: string
	/** Set only for money metrics. */
	currency?: string
}
